import { observer } from "mobx-react";
import * as React from "react";
import app from "../../../application/App";
import { CustomPage2, ExternalPage, FormPage, GridPage, LayoutPage } from "../../../application/Page";

import { Alert, LinearProgress } from "@mui/material";
import { DataViewComponent } from "../DataGridComponent";
import { DataViewBarChartComponent } from "../DataViewComponent/DataViewBarChart";
import { VerticalForm } from "../VerticalForm";
import { ExternalPageComponent } from "./ExternalPageComponent";

interface IState {
    error?: any;
    errorInfo?: any;
}

interface IProps {
    embedded?: boolean;
    padding?: number;
}

class ErrorBoundary extends React.Component<{ children?: React.ReactNode }, IState> {
    state: IState = {};

    componentDidCatch(error: any, errorInfo: any) {
        this.setState({ error, errorInfo });
    }

    render() {
        const { error } = this.state;
        if (error) {
            return <Alert severity="error">{error.toString()}</Alert>;
        }
        return (this.props.children as React.ReactElement) ?? null;
    }
}

const PageComponentContent: React.FC<IProps> = observer((props: IProps) => {
    const { embedded, padding } = props;
    const appInstance = React.useMemo(() => app(), []);

    const renderForm = (page: FormPage, _args: any[]) => {
        return (
            <VerticalForm
                key={page.name}
                form={page.form}
                style={!!embedded ? undefined : { padding: padding || 24 }}
            />
        );
    };

    const renderGrid = (page: GridPage, _args: any[]) => {
        if (!page.data.grid) {
            return <LinearProgress style={{ width: "100%" }} />;
        }

        // TODO: use new URL(...)
        let url = page.data.grid;
        const index = url.indexOf("#");
        const barChart = url.indexOf("barChart", index) > 0;
        if (index > 0) {
            url = url.substr(0, index);
        }

        const style = !!embedded
            ? undefined
            : appInstance.innerWidth < 600
            ? { padding: 6 }
            : { padding: padding !== undefined ? padding : 24 };

        if (barChart) {
            return <DataViewBarChartComponent view={url} updateAppTitle={true} style={style} />;
        }

        return (
            <DataViewComponent
                key={page.name}
                view={url}
                args={appInstance.args}
                updateAppTitle={true}
                style={style}
            />
        );
    };

    const renderExternal = (page: ExternalPage, _args: any[]) => {
        return <ExternalPageComponent page={page} />;
    };

    const page = appInstance.page;
    const args = appInstance.args;

    if (page instanceof FormPage) return renderForm(page, args);
    if (page instanceof GridPage) return renderGrid(page, args);
    if (page instanceof CustomPage2) return page.render(args, embedded);
    if (page instanceof LayoutPage) return page.render(args, embedded);
    if (page instanceof ExternalPage) return renderExternal(page, args);

    return <div>{page ? page.name : ""}</div>;
});

export default function PageComponent(props: IProps) {
    return (
        <ErrorBoundary>
            <PageComponentContent {...props} />
        </ErrorBoundary>
    );
}
